/**
 * What the score on a report is built from, said before the report is bought.
 *
 * Pure functions with tests, the treatment `route.ts`, `views.ts`,
 * `alerts.ts`, `scoreScope.ts` and `pricing.ts` get, and for the same reason:
 * a legend that misstates how a number was reached is worse than no legend,
 * because it is read as authoritative.
 *
 * **A building's score is not the sum of its units' scores, and this is the
 * whole reason the module exists.** The two vocabularies look alike and are
 * different in three ways at once:
 *
 * - They are *different weights*. An unsafe-structure case is worth 40 to a
 *   parcel and 40 to a building, but a lien is 30 to a parcel and 25 to a
 *   building, and seven of the twelve unit signals do not move a building's
 *   score at all.
 * - The building weights are **per case**, not per building. `unsafe`, `lien`
 *   and `expired_permit` are multiplied by the number of units carrying them,
 *   so a tower with nine unsafe-structure cases scores 360 from that term
 *   alone — which is why a building can score 934 where no parcel exceeds 185.
 * - The milestone term is added **once**.
 *
 * So the legend is chosen by the report's scope rather than shown whole. A
 * buildings report shown the parcel weights would be a false statement about
 * how the number printed in the document was computed.
 *
 * Nothing here holds a weight of its own: every number arrives from
 * `/api/scoring`, which serves the constants the pipeline scored with.
 */

import type { ScoringRules } from "../api/types";
import type { ReportScope } from "./reportParams";

export interface LegendRow {
  key: string;
  label: string;
  points: number;
  /** "per unit carrying it" where the term is counted per case. */
  qualifier: string;
}

export interface LegendSection {
  /** Which records this applies to — a report may contain both kinds. */
  scope: "buildings" | "units";
  heading: string;
  /** How the terms combine, in a sentence. */
  note: string;
  rows: LegendRow[];
}

/**
 * The legend for a report of this scope, heaviest term first.
 *
 * Returns an empty list until `/api/scoring` has answered: a legend that
 * renders with no numbers in it reads as "these signals are worth nothing",
 * which is the opposite of what a pending request means.
 */
export function scoreLegend(
  rules: ScoringRules | null,
  scope: ReportScope,
): LegendSection[] {
  if (!rules) return [];
  const sections: LegendSection[] = [];

  if (scope !== "units") {
    sections.push({
      scope: "buildings",
      heading: "What a building scores",
      note:
        "A building's score is rolled up from the cases in it, so the first " +
        "three count once for every unit carrying them. It is not the sum of " +
        "its units' scores.",
      rows: rules.building_signals.map((signal) => ({
        key: signal.key,
        label: signal.label,
        points: signal.points,
        qualifier: signal.per_unit ? "per unit carrying it" : "once",
      })),
    });
  }

  if (scope !== "buildings") {
    sections.push({
      scope: "units",
      heading: "What a parcel scores",
      note: "Each signal a parcel carries adds its points, once.",
      rows: rules.signals.map((signal) => ({
        key: signal.flag,
        label: signal.label,
        points: signal.points,
        qualifier: "",
      })),
    });
  }

  return sections;
}

/**
 * The most a record of this scope can score, for the "out of" beside a number.
 *
 * Deliberately **not** computed for buildings: the per-case terms have no
 * ceiling, since a building scores 40 for every unsafe-structure case in it
 * and there is no bound on how many that is. A total presented there would be
 * a maximum that records already exceed — the live data holds a building at
 * 934 against a five-term table summing to 115.
 */
export function maxParcelScore(rules: ScoringRules | null): number | null {
  if (!rules) return null;
  return rules.signals.reduce((total, signal) => total + signal.points, 0);
}
